import { useState, useEffect, useRef } from 'react'
import LocalizedLink from '@/components/layout/LocalizedLink'
import Button from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import { useTranslation } from 'react-i18next'
import homePageApiService, { PublicHeroSlide } from '@/services/homePageApiService'
import { getImageUrl, resolveAssetPath } from '@/utils/basePath'
import { getLocalizedValue } from '@sakeena/shared'
import { useCurrentLanguage, getLocalizedContent } from '@/utils/contentVisibility'

export default function HeroSection() {
  const { t } = useTranslation()
  const currentLang = useCurrentLanguage()
  const isRTL = currentLang.toLowerCase().startsWith('ar') || currentLang.toLowerCase().startsWith('ur')

  const [slides, setSlides] = useState<PublicHeroSlide[]>([])
  const [loading, setLoading] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [failedImageIds, setFailedImageIds] = useState<Set<string>>(new Set())
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)
  const sliderContainerRef = useRef<HTMLDivElement>(null)

  // فرض خلفية شفافة على حاوية السلايدر (يتغلب على أي inline style أو !important يضيف لونًا)
  useEffect(() => {
    const el = sliderContainerRef.current
    if (el) el.style.setProperty('background-color', 'transparent', 'important')
  }, [loading, slides.length])

  // Fetch slides from API (US-2.B1). Filter by PUBLISHED translation in selected language.
  useEffect(() => {
    const staticSlides: PublicHeroSlide[] = [
      {
        id: 'static-1',
        imageUrl: '/assets/1.png',
        title: t('hero.slide1.title', 'مرحبًا بك في سكينة'),
        description: t('hero.slide1.description', 'مساحتك للسكينة والطمأنينة مع محتوى إيماني هادئ.'),
        primaryButtonText: t('hero.slide1.primary', 'استكشف القرآن'),
        primaryButtonLink: '/quran',
        secondaryButtonText: t('hero.slide1.secondary', 'عن سكينة'),
        secondaryButtonLink: '/about',
        order: 1,
        isActive: true,
      },
      {
        id: 'static-2',
        imageUrl: '/assets/2.png',
        title: t('hero.slide2.title', 'اذكر الله يطمئن قلبك'),
        description: t('hero.slide2.description', 'استمع للأذكار، والمحاضرات، وكل ما ينعش روحك.'),
        primaryButtonText: t('hero.slide2.primary', 'ابدأ الاستماع'),
        primaryButtonLink: '/videos',
        secondaryButtonText: t('hero.slide2.secondary', 'المكتبة'),
        secondaryButtonLink: '/library',
        order: 2,
        isActive: true,
      },
      {
        id: 'static-3',
        imageUrl: '/assets/4.png',
        title: t('hero.slide3.title', 'ركن هادئ لقلبك'),
        description: t('hero.slide3.description', 'تأملات، دروس، وخدمات تعينك على الطاعة.'),
        primaryButtonText: t('hero.slide3.primary', 'الخدمات'),
        primaryButtonLink: '/services',
        secondaryButtonText: t('hero.slide3.secondary', 'تعرّف علينا'),
        secondaryButtonLink: '/about',
        order: 3,
        isActive: true,
      },
    ]

    const fetchSlides = async () => {
      try {
        setLoading(true)
        let raw: PublicHeroSlide[] = []
        try {
          raw = await homePageApiService.getActiveHeroSlides(currentLang)
        } catch {
          raw = []
        }
        // API returns only slides that have title in the requested language. If empty (e.g. EN when DB has only AR), use static fallback so banners always show.
        let next: PublicHeroSlide[]
        if (raw.length > 0) {
          const sorted = [...raw].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          next = sorted
        } else {
          next = staticSlides
        }
        setSlides(next)
        setFailedImageIds(new Set())
      } catch (err) {
        console.error('Failed to fetch hero slides:', err)
        setSlides(staticSlides)
      } finally {
        setLoading(false)
      }
    }

    fetchSlides()
  }, [currentLang, t])

  // Auto-rotate slides
  useEffect(() => {
    if (slides.length < 2 || isPaused) return
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [slides.length, isPaused])

  const nextSlide = () => {
    if (slides.length === 0) return
    setCurrentIndex((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    if (slides.length === 0) return
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length)
  }

  // Touch swipe handlers for mobile (Safari-compatible)
  const minSwipeDistance = 50

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    const touch = e.touches[0] || e.targetTouches[0]
    if (touch) {
      setTouchStart(touch.clientX)
    }
  }

  const onTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0] || e.targetTouches[0]
    if (touch) {
      setTouchEnd(touch.clientX)
    }
  }

  const onTouchEnd = (_e: React.TouchEvent) => {
    if (!touchStart || !touchEnd) {
      setTouchStart(null)
      setTouchEnd(null)
      return
    }
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      isRTL ? prevSlide() : nextSlide()
    }
    if (isRightSwipe) {
      isRTL ? nextSlide() : prevSlide()
    }

    setTouchStart(null)
    setTouchEnd(null)
  }

  if (!loading && slides.length === 0) return null

  // كل الشرائح ما عدا الأخيرة: 15% يسار (فراغ أبيض) + 68% شريحة + 2% فجوة. الشريحة الأخيرة فقط: كاملة + السابقة على اليسار، بدون فراغ أبيض
  const leftPeekPercent = 15
  const slidePercent = 68
  const gapPercent = 2
  const trackWidthPercent =
    leftPeekPercent + slides.length * slidePercent + (slides.length - 1) * gapPercent
  const slideFraction = (slidePercent / trackWidthPercent) * 100
  const gapFraction = (gapPercent / trackWidthPercent) * 100
  const spacerFraction = (leftPeekPercent / trackWidthPercent) * 100
  // ترجمة عادية: الشريحة الحالية بعد السبيسر
  const rawTranslate = (slideFraction + gapFraction) * currentIndex - spacerFraction
  const isLastSlide = slides.length >= 2 && currentIndex === slides.length - 1
  const lastSlideStartInTrack =
    spacerFraction + (slides.length - 1) * (slideFraction + gapFraction)
  // آخر slide: الشريحة الأخيرة كاملة + الباقي من السابقة، بدون إظهار السبيسر الأبيض. نفس المنطق لـ RTL و LTR (قيمة موجبة ثم نعكس في الـ style)
  const lastSlideTranslateRaw =
    lastSlideStartInTrack - ((100 - slidePercent) * 100) / trackWidthPercent
  const lastSlideTranslate = Math.max(spacerFraction, lastSlideTranslateRaw)
  const clampedTranslate = isLastSlide
    ? Math.max(0, lastSlideTranslate)
    : Math.max(0, rawTranslate)

  return (
    <section className="hero-section-bg relative py-3 sm:py-4 md:py-6 lg:py-8 xl:py-10">
      <div className="w-full overflow-hidden bg-transparent">
        {loading && (
          <div className="w-full h-[200px] xs:h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px] xl:h-[480px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 border-b-2 border-primary-600" />
          </div>
        )}

        {!loading && slides.length > 0 && (
          <div
            className="w-full touch-pan-y bg-transparent"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{ touchAction: 'pan-y pinch-zoom' }}
          >
            {/* حاوية السلايدر شفافة — class + ref يتغلبان على أي inline أو !important يطبق لون خلفية */}
            <div
              ref={sliderContainerRef}
              className="hero-slider-bg-transparent relative overflow-hidden rounded-xl sm:rounded-xl md:rounded-2xl bg-transparent"
              style={{ touchAction: 'pan-x', WebkitOverflowScrolling: 'touch' }}
            >
              <div
                dir="ltr"
                className="flex transition-transform duration-[1200ms] ease-in-out"
                style={{
                  width: `${trackWidthPercent}%`,
                  gap: `${gapFraction}%`,
                  transform: isRTL
                    ? `translate3d(${clampedTranslate}%, 0, 0)`
                    : `translate3d(-${clampedTranslate}%, 0, 0)`,
                  WebkitTransform: isRTL
                    ? `translate3d(${clampedTranslate}%, 0, 0)`
                    : `translate3d(-${clampedTranslate}%, 0, 0)`,
                  willChange: 'transform',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                }}
              >
                {/* سبيسر يسار — شفاف */}
                <div
                  className="flex-shrink-0 overflow-hidden bg-transparent"
                  style={{
                    width: `${spacerFraction}%`,
                    flexBasis: `${spacerFraction}%`,
                    minWidth: 0,
                  }}
                  aria-hidden
                />
                {slides.map((slide, index) => {
                  const imageFailed = failedImageIds.has(slide.id)
                  const imageUrlResolved =
                    typeof slide.imageUrl === 'string'
                      ? (slide.imageUrl || '').trim()
                      : getLocalizedValue(
                          (slide.imageUrl as Record<string, string>) ?? {},
                          currentLang
                        ) || ''
                  const rawSrc = imageUrlResolved
                    ? imageUrlResolved.startsWith('/assets/')
                      ? resolveAssetPath(imageUrlResolved)
                      : getImageUrl(imageUrlResolved)
                    : ''
                  const showImage = !imageFailed && rawSrc

                  return (
                    <div
                      key={slide.id}
                    className="flex-shrink-0 group overflow-hidden rounded-xl sm:rounded-xl md:rounded-2xl bg-transparent"
                    style={{
                      width: `${slideFraction}%`,
                      flexBasis: `${slideFraction}%`,
                      minWidth: 0,
                    }}
                    >
                      <div className="relative h-[200px] xs:h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px] xl:h-[480px] 2xl:h-[520px] w-full">
                        {showImage ? (
                          <img
                            src={rawSrc}
                            alt={getLocalizedValue(slide.title, currentLang)}
                            className={`w-full h-full object-cover transition-transform duration-[5000ms] ease-out ${
                              index === currentIndex ? 'scale-105 sm:scale-110' : 'scale-100'
                            }`}
                            loading={index === 0 ? 'eager' : 'lazy'}
                            onError={() => setFailedImageIds(prev => new Set(prev).add(slide.id))}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900" />
                        )}

                        {/* Softer gradient overlay so image is visible; lighter when no image so text stands out */}
                        <div className={`absolute inset-0 ${showImage ? 'bg-gradient-to-t from-black/70 via-black/40 to-black/10' : 'bg-black/20'}`} />

                        {/* Centered content */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3 xs:px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12">
                          <div className="max-w-3xl mx-auto space-y-2 xs:space-y-2.5 sm:space-y-3 md:space-y-4 w-full">
                            {/* Title always visible */}
                            <h2 className="text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold text-white leading-tight drop-shadow-lg px-1 sm:px-2">
                              {getLocalizedValue(slide.title, currentLang)}
                            </h2>

                            {/* Paragraph + buttons */}
                            <div
                              className="
                                space-y-2 xs:space-y-2.5 sm:space-y-3 md:space-y-4
                                opacity-100 translate-y-0
                                md:opacity-0 md:translate-y-3
                                md:group-hover:opacity-100 md:group-hover:translate-y-0
                                md:transition-all md:duration-500
                              "
                            >
                              <p className="text-[10px] xs:text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-white/90 leading-relaxed drop-shadow-md px-1 sm:px-2 max-w-2xl mx-auto">
                                {getLocalizedValue(slide.description, currentLang)}
                              </p>

                              <div className="flex flex-row gap-1.5 xs:gap-2 sm:gap-2.5 md:gap-3 justify-center items-center px-1 sm:px-2 flex-wrap">
                                {/* Primary CTA – hide if label missing for current/fallback language */}
                                {(getLocalizedContent(slide.primaryButtonText, currentLang)?.trim().length ?? 0) > 0 &&
                                  (slide.primaryButtonLink && slide.primaryButtonLink !== '#' ? (
                                    <LocalizedLink
                                      to={slide.primaryButtonLink}
                                      className="flex-1 xs:flex-none min-w-[100px] xs:min-w-[120px] sm:min-w-[140px]"
                                    >
                                      <Button
                                        key={`${slide.id}-primary`}
                                        size="lg"
                                        variant="primary"
                                        className="w-full shadow-xl text-xs xs:text-sm sm:text-base px-2 xs:px-3 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2.5 md:py-3"
                                      >
                                        {getLocalizedValue(slide.primaryButtonText, currentLang)}
                                      </Button>
                                    </LocalizedLink>
                                  ) : (
                                    <Button
                                      key={`${slide.id}-primary`}
                                      size="lg"
                                      variant="primary"
                                      className="flex-1 xs:flex-none min-w-[100px] xs:min-w-[120px] sm:min-w-[140px] shadow-xl text-xs xs:text-sm sm:text-base px-2 xs:px-3 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2.5 md:py-3"
                                    >
                                      {getLocalizedValue(slide.primaryButtonText, currentLang)}
                                    </Button>
                                  ))}

                                {/* Secondary CTA – hide if label missing for current/fallback language */}
                                {(getLocalizedContent(slide.secondaryButtonText, currentLang)?.trim().length ?? 0) > 0 &&
                                  (slide.secondaryButtonLink && slide.secondaryButtonLink !== '#' ? (
                                    <LocalizedLink
                                      to={slide.secondaryButtonLink}
                                      className="flex-1 xs:flex-none min-w-[100px] xs:min-w-[120px] sm:min-w-[140px]"
                                    >
                                      <Button
                                        key={`${slide.id}-secondary`}
                                        size="lg"
                                        variant="outline"
                                        className="w-full bg-white/10 backdrop-blur-sm text-white border-white/50 hover:bg-white/20 text-xs xs:text-sm sm:text-base px-2 xs:px-3 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2.5 md:py-3"
                                      >
                                        {getLocalizedValue(slide.secondaryButtonText, currentLang)}
                                      </Button>
                                    </LocalizedLink>
                                  ) : (
                                    <Button
                                      key={`${slide.id}-secondary`}
                                      size="lg"
                                      variant="outline"
                                      className="flex-1 xs:flex-none min-w-[100px] xs:min-w-[120px] sm:min-w-[140px] bg-white/10 backdrop-blur-sm text-white border-white/50 hover:bg-white/20 text-xs xs:text-sm sm:text-base px-2 xs:px-3 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2.5 md:py-3"
                                    >
                                      {getLocalizedValue(slide.secondaryButtonText, currentLang)}
                                    </Button>
                                  ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* لا arrows — حسب طلب التصميم */}
            </div>

            {/* النقاط تحت السلايدر — نفس الخلفية الشفافة عشان مفيش شريط أبيض */}
            {slides.length > 1 && (
              <div className="mt-2 xs:mt-2.5 sm:mt-3 md:mt-4 flex justify-center gap-2 bg-transparent">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={cn(
                      'w-2.5 h-2.5 rounded-full transition-all duration-300 touch-manipulation',
                      index === currentIndex ? 'bg-gray-800 dark:bg-gray-200' : 'bg-gray-400 dark:bg-gray-500'
                    )}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
